import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import SelectDropdown from 'components/base/select-dropdown';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { ArrowDownIcon } from 'cokitchen-icons';
import { weightMeasurement } from 'utils/constants';
import '../new-procurement/styles.scss';
import useDashboardContext from 'contexts/dashboard.context';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { UpdateRejectedProcurementType } from 'models/procurement.model';

const UpdateRejectedProcurementForm = ({
  data,
  index,
  setValue,
  removeItem,
  canWrite,
  setVendorIngredientIdsToDelete
}: {
  data: UpdateRejectedProcurementType;
  index: number;
  setValue: (index: number, field: keyof UpdateRejectedProcurementType, value: string) => void;
  removeItem: (index: number) => void;
  canWrite: boolean;
  setVendorIngredientIdsToDelete: Dispatch<SetStateAction<string[]>>;
}) => {
  const { isFetchingFormStoreItems, formStoreItems, formVendorsList } = useDashboardContext();
  const getIngredient = (id: string) => formStoreItems?.find((ingredient) => ingredient.id === id);
  const [isFetchingVendorIngredients, setIsFetchingVendorIngredients] = useState(false);
  const [vendorIngredients, setVendorIngredients] = useState<
    { id: string; name: string; price: string; unit_type: string; vendor_id: string }[]
  >([]);
  const getVendorNameAndPrice = (id: string) => formVendorsList?.find((vendor) => vendor.id === id);

  const fetchVendorIngredients = async (id: string) => {
    try {
      setIsFetchingVendorIngredients(true);
      const response = await apiService.get(`vendors/ingredients?ingredient_id=${id}`);
      if (response) {
        setVendorIngredients(
          response?.data?.results.map((vendor_ingredient: any) => {
            return {
              id: vendor_ingredient.id,
              name: vendor_ingredient.ingredient.name,
              price: '₦ ' + Number(vendor_ingredient.gram_price).toLocaleString(),
              unit_type: vendor_ingredient.ingredient.unit_type,
              vendor_id: vendor_ingredient.vendor_id
            };
          })
        );
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching vendor ingredients'}
          type='error'
        />
      );
    } finally {
      setIsFetchingVendorIngredients(false);
    }
  };

  useEffect(() => {
    data.ingredient_id && fetchVendorIngredients(data.ingredient_id);
  }, []);

  const removeProcurementItem = () => {
    setVendorIngredientIdsToDelete((prev) => [...prev, data.id]);
    removeItem(index);
  };

  const unitType = getIngredient(data?.ingredient_id)?.unit_type;

  return (
    <div className={`add__procurement pb-4 ${index > 0 ? 'pt-6' : ''} border-b border-[#EDEDEE]`}>
      <div>
        <div className='grid grid-cols-2 gap-6 mb-6 flex-wrap'>
          <div>
            <p className='font-medium mb-2'>Item name</p>
            <Dropdown
              content={
                <div className='flex justify-between items-center bg-[#FAFAFA] border-b-[2px] border-b-[#D5D6D7] min-h-[44px] h-auto px-[14px]'>
                  {data?.ingredient_id ? (
                    <div className='text-[#2e3031] flex gap-1 items-center'>
                      <span className='text-[#212224] text-sm'>
                        {getIngredient(data?.ingredient_id)?.name}
                      </span>
                    </div>
                  ) : (
                    <span className='text-[#B5B8BA]'>Select Item name</span>
                  )}
                  <ArrowDownIcon className='h-5 w-5 text-[#95999C]' />
                </div>
              }>
              <DropdownList>
                {isFetchingFormStoreItems ? (
                  <div>
                    <ActivityIndicator />
                  </div>
                ) : (
                  formStoreItems.map((item) => (
                    <DropdownItem key={item.id}>
                      <div
                        className='flex gap-1 items-center'
                        onClick={() => {
                          if (!canWrite) return;
                          setValue(index, 'ingredient_id', item.id);
                          setValue(index, 'vendor_ingredient_unit_type', item.unit_type);
                          setVendorIngredients([]);
                          setValue(index, 'vendor_id', '');
                          item.id && fetchVendorIngredients(item.id);
                        }}>
                        <span className='text-[#212224] text-sm'>{item.name}</span>
                        {/* <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />
                        <span className='text-[#6F7376] text-xs'> {item.price}</span> */}
                      </div>
                    </DropdownItem>
                  ))
                )}
              </DropdownList>
            </Dropdown>
          </div>
          <div className='relative'>
            <Input
              design='bordered'
              label='Quantity'
              placeholder='00'
              value={data.quantity}
              disabled={!canWrite}
              onChange={(e) => setValue(index, 'quantity', e.target.value)}
            />
            <div className='absolute w-[50px] h-[20px] z-10 rounded-[2px] shadow bottom-[20%] right-[5%]'>
              <Dropdown
                disabled
                content={
                  <div className='h-5 p-1 text-[11px] flex justify-between items-center gap-[2px] cursor-pointer measurement_dropdown'>
                    <span className='text-[#6F7376] truncate capitalize'>
                      {
                        <span className={`text-[#6F7376] truncate ${unitType ? 'capitalize' : ''}`}>
                          {unitType ?? 'Unit(s)'}
                        </span>
                      }
                    </span>
                    {/* <ArrowDownIcon className='w-5 h-5 text-[#96999C]' /> */}
                  </div>
                }>
                <DropdownList>
                  {weightMeasurement.map((item) => (
                    <DropdownItem
                      key={item.value}
                      onClick={() => {
                        return;
                      }}>
                      {item.name}
                    </DropdownItem>
                  ))}
                </DropdownList>
              </Dropdown>
            </div>
          </div>
          <div className='col-span-2'>
            <SelectDropdown
              options={
                !data.ingredient_id
                  ? [{ id: '', name: 'Select an ingredient' }]
                  : vendorIngredients.length > 0
                  ? vendorIngredients.map((vendor_ingredient) => {
                      return {
                        id: vendor_ingredient.id,
                        name: getVendorNameAndPrice(vendor_ingredient.vendor_id)?.name || '',
                        price: vendor_ingredient.price,
                        unit_type: vendor_ingredient.unit_type,
                        vendor_id: vendor_ingredient.vendor_id
                      };
                    })
                  : [{ id: '', name: 'Ingredient not available at any vendor' }]
              }
              loading_options={isFetchingVendorIngredients}
              value={getVendorNameAndPrice(data.vendor_id)?.name}
              price_value={data.vendor_ingredient_price}
              placeholder='Select a vendor'
              setValue={(value: {
                id: string;
                name: string;
                price: string;
                unit_type: string;
                vendor_id: string;
                vendor_name: string;
              }) => {
                if (!canWrite) return;
                setValue(index, 'vendor_id', value.vendor_id);
                setValue(index, 'vendor_ingredient_price', value.price);
                setValue(index, 'vendor_ingredient_unit_type', value.unit_type);
              }}
              label='Vendor'
            />
          </div>
        </div>
        {canWrite && index >= 1 && (
          <button
            onClick={() => (data.id ? removeProcurementItem() : removeItem(index))}
            className='text-[#E60A2B] mt-2 font-medium text-sm '>
            - Remove Item
          </button>
        )}
      </div>
    </div>
  );
};

export default UpdateRejectedProcurementForm;
